import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Building2, Mail, Phone, User, Tag, DollarSign, Percent,
} from 'lucide-react';
import { formatCurrency, formatDateTime } from '../../utils/api';

const InfoItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-muted-foreground mt-0.5" />
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium">{value || '-'}</p>
    </div>
  </div>
);

export const LeadOverviewCard = ({ lead }) => {
  // Phase 40.6 — Closed Deal Value is the effective amount used for all commission math.
  const effectiveDealValue = lead.closed_deal_value ?? lead.deal_value ?? 0;
  // Phase 36.3 — Template-driven commissions take priority; fall back to the
  // server-computed commission_breakdown.vyapaar_percentage (which already applies
  // the company default / 15% fallback). NOTE: partner_commission_percent is the
  // REFERRAL commission alias, not Vyapaar's own % — must never be used here
  // (Phase 41.6 fix — was previously inflating the displayed Vyapaar % whenever a
  // referral commission was set on the lead).
  const vyapaarPct = lead.commission_override
    ?? lead.commission_breakdown?.vyapaar_percentage
    ?? 15;
  const vyapaarAmount = effectiveDealValue * vyapaarPct / 100;
  // Phase 40.6 — combine Company + Person into one line wherever a partner is shown
  const partnerDisplay = lead.selling_partner_company_name && lead.lead_owner_name
    ? `${lead.selling_partner_company_name} — ${lead.lead_owner_name}`
    : (lead.selling_partner_company_name || lead.lead_owner_name || lead.selling_partner_name);
  // Phase 41.2 — same Company — Person combined display for the referring Sales Associate / Partner
  const referredByDisplay = lead.sales_associate_company_name && lead.sales_associate_name
    ? `${lead.sales_associate_company_name} — ${lead.sales_associate_name}`
    : (lead.sales_associate_company_name || lead.sales_associate_name);
  const dealValueDiffers = lead.closed_deal_value != null && lead.deal_value != null && lead.closed_deal_value !== lead.deal_value;
  return (
  <Card>
    <CardHeader>
      <div className="flex items-start justify-between">
        <div>
          <CardTitle className="text-2xl">{lead.title}</CardTitle>
          <CardDescription className="mt-1">
            Created {formatDateTime(lead.created_at)} by {lead.created_by_name}
          </CardDescription>
        </div>
        <Badge
          className="text-sm"
          style={{
            backgroundColor: `${lead.status_color}20`,
            color: lead.status_color,
            borderColor: lead.status_color,
          }}
        >
          {lead.status_name || 'New'}
        </Badge>
      </div>
    </CardHeader>
    <CardContent className="space-y-4">
      {lead.description && <p className="text-muted-foreground">{lead.description}</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        <InfoItem icon={Tag} label="Category" value={lead.primary_category_name} />
        {lead.secondary_category_name && (
          <InfoItem icon={Tag} label="Sub-category" value={lead.secondary_category_name} />
        )}
        <InfoItem icon={DollarSign} label="Deal Value" value={formatCurrency(lead.deal_value)} />
        {dealValueDiffers && (
          <InfoItem icon={DollarSign} label="Closed Deal Value" value={formatCurrency(lead.closed_deal_value)} />
        )}
        {/* Phase 36.3 — Vyapaar commission (template-driven, falls back to legacy fields) */}
        {!!effectiveDealValue && (
          <InfoItem
            icon={Percent}
            label={`Vyapaar Commission (${vyapaarPct}%)`}
            value={formatCurrency(vyapaarAmount)}
          />
        )}
        {partnerDisplay && (
          <InfoItem icon={Building2} label="Selling Partner" value={partnerDisplay} />
        )}
        {lead.sales_associate_name && (
          <InfoItem icon={User} label="Referred By" value={referredByDisplay} />
        )}
      </div>
    </CardContent>
  </Card>
  );
};

export const CustomerInfoCard = ({ lead }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <User className="w-5 h-5 text-primary" />
        Customer Information
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid sm:grid-cols-2 gap-4">
        <InfoItem icon={User} label="Name" value={lead.customer_name} />
        <InfoItem icon={Mail} label="Email" value={lead.customer_email} />
        {lead.customer_phone && (
          <InfoItem icon={Phone} label="Phone" value={lead.customer_phone} />
        )}
        {lead.customer_company && (
          <InfoItem icon={Building2} label="Company" value={lead.customer_company} />
        )}
      </div>
    </CardContent>
  </Card>
);

export const CommissionBreakdownCard = ({ lead }) => {
  // Phase 40.6 — rewritten to use Closed Deal Value (the effective amount) and to
  // surface the referral partner's NAME + commission amount, not just a %.
  const dealValue = lead.closed_deal_value ?? lead.deal_value ?? 0;
  if (!dealValue) return null;
  const vyapaarPct = lead.commission_override ?? lead.commission_breakdown?.vyapaar_percentage ?? 15;
  const vyapaarShare = dealValue * vyapaarPct / 100;
  const partnerKeeps = dealValue - vyapaarShare;
  const referralPct = lead.referral_commission_percent ?? 0;
  const referralAmount = lead.referral_commission_amount ?? (vyapaarShare * referralPct / 100);
  const netToVyapaar = vyapaarShare - (referralPct > 0 ? referralAmount : 0);
  return (
    <Card data-testid="commission-breakdown">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="w-5 h-5 text-primary" />
          Commission Breakdown
        </CardTitle>
        <CardDescription>Transparent view of how commission is distributed</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-2">
            <span className="text-muted-foreground">Closed Deal Value</span>
            <span className="font-semibold text-lg">{formatCurrency(dealValue)}</span>
          </div>
          <Separator />
          <div className="flex justify-between items-center py-2">
            <span className="text-muted-foreground">Selling Partner keeps ({(100 - vyapaarPct).toFixed(1)}%)</span>
            <span className="font-medium text-green-600">{formatCurrency(partnerKeeps)}</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-muted-foreground">Vyapaar Commission ({vyapaarPct}%)</span>
            <span className="font-medium text-primary">{formatCurrency(vyapaarShare)}</span>
          </div>
          {referralPct > 0 && (
            <div className="border-l-2 border-indigo-300 dark:border-indigo-700 ml-4 pl-3 space-y-1.5 py-1" data-testid="referral-commission-details">
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">
                  Referral payout to <span className="font-medium">{lead.sales_associate_name || 'Referrer'}</span>
                  {lead.referral_commission_level_name ? ` (${lead.referral_commission_level_name}, ${referralPct}%)` : ` (${referralPct}%)`}
                </span>
                <span className="font-medium text-rose-600 dark:text-rose-300">– {formatCurrency(referralAmount)}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Net to Vyapaar</span>
                <span className="font-semibold text-emerald-700 dark:text-emerald-300">{formatCurrency(netToVyapaar)}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
